import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { EFile, EFileRecord } from '@tabservice/tabfile-client';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm';
import { useToast } from '@/components/ui/toast';
import { queuedForOf, type FixQueuedFor } from './record-fix';
import { newIdempotencyKey } from '@/features/new-filing/idempotency';
import { errorMessage, tabfile } from '@/lib/api';
import { formatCount } from '@/lib/format';

const wording: Record<FixQueuedFor, { noun: string; toast: string; detail: string; sentence: string }> = {
  correction: {
    noun: 'correction',
    toast: 'Corrections submitted',
    detail: 'The corrected records are queued for the IRS.',
    sentence: 'These records will be filed as a correction against this e-file.',
  },
  replacement: {
    noun: 'replacement',
    toast: 'Replacement submitted',
    detail: 'The fixed records are queued for the IRS.',
    sentence: 'These records will be filed as a replacement of this e-file.',
  },
};

export interface SubmitQueuedFixesProps {
  filing: Pick<EFile, 'id' | 'status' | 'queuedFixes'>;
  /** The records the page has loaded, so the confirmation can name them. */
  records: EFileRecord[];
  onSubmitted: (created: EFile) => void;
}

/**
 * The other half of the record drawer. Each record queues its own fix; this
 * sends the whole queue as one e-file, because the IRS takes a correction or a
 * replacement as a transmission rather than as a record at a time.
 */
export function SubmitQueuedFixes({ filing, records, onSubmitted }: SubmitQueuedFixesProps) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const idempotencyKey = useRef(newIdempotencyKey());

  const queued = records.filter((record) => record.fix?.state === 'queued');
  const total = filing.queuedFixes ?? queued.length;
  const kind = queuedForOf(queued, filing.status === 'rejected' ? 'replacement' : 'correction');
  const words = wording[kind];

  const submit = useMutation({
    mutationFn: () => {
      const params = { path: { efileId: filing.id } };
      const body = { fromQueued: true as const };
      return kind === 'correction'
        ? tabfile.POST('/v1/e-files/{efileId}/corrections', {
            params,
            body,
            idempotencyKey: idempotencyKey.current,
          })
        : tabfile.POST('/v1/e-files/{efileId}/replacements', {
            params,
            body,
            idempotencyKey: idempotencyKey.current,
          });
    },
    onSuccess: (created) => {
      setOpen(false);
      toast.success(words.toast, words.detail);
      onSubmitted(created);
    },
    onError: (error) => toast.error(`Could not submit the queued ${words.noun}`, errorMessage(error)),
  });

  if (total < 1) return null;

  const unlisted = total - queued.length;

  return (
    <>
      <Button data-tour="submit-queued" variant="primary" onClick={() => setOpen(true)}>
        <Send className="h-4 w-4" aria-hidden="true" />
        Submit {formatCount(total)} queued {total === 1 ? 'fix' : 'fixes'}
      </Button>

      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title={`Submit ${formatCount(total)} queued ${total === 1 ? 'fix' : 'fixes'}`}
        description={words.sentence}
        confirmLabel={kind === 'correction' ? 'Submit corrections' : 'Submit replacement'}
        pending={submit.isPending}
        onConfirm={() => submit.mutate()}
        body={
          <ul className="flex flex-col divide-y divide-[color:var(--ts-row-border)]">
            {queued.map((record) => (
              <li key={record.recordRef} className="flex flex-wrap items-center justify-between gap-3 py-2.5">
                <span className="text-base font-medium text-gray-3">{record.recordRef}</span>
                <span className="text-base text-muted-foreground">
                  {record.recipientName ?? 'Name not on file'}
                </span>
              </li>
            ))}
            {unlisted > 0 ? (
              <li className="py-2.5 text-base text-muted-foreground">
                and {formatCount(unlisted)} more queued on later pages
              </li>
            ) : null}
          </ul>
        }
      />
    </>
  );
}
